import { inject, Injectable } from '@angular/core';
import { GenericApiResponseModel, GenericApiService } from '@core/generic-api/data-access';
import { map, Observable, tap } from 'rxjs';
import { CountryModel, DataSetupModel, NationalityModel } from '@core/data-access';
import { CountryFacade, DataSetupFacade, NationalityFacade } from '../store';
import { titleCase } from '@core/util';

@Injectable({ providedIn: 'root' })
export class DataSetupApiService {
  private readonly COUNTRIES = 'countries';
  private readonly NATIONALITIES = 'nationalities';
  private readonly DATA_SETUP = 'cases-settings/data-setup';
  private readonly countryFacade = inject(CountryFacade);
  private readonly nationalityFacade = inject(NationalityFacade);
  private readonly dataSetupFacade = inject(DataSetupFacade);
  private readonly api = inject(GenericApiService);

  getCountries(): Observable<GenericApiResponseModel<CountryModel>> {
    return this.api.get(this.COUNTRIES, { limit: 999 }).pipe(
      map(data => {
        const countries = data?.items;
        if (!countries || countries.length === 0) return new GenericApiResponseModel(CountryModel, data);
        const topListCountryCodes = ['MY', 'SG'];
        topListCountryCodes.forEach(item => {
          const index = countries.findIndex((country: CountryModel) => country.alpha2 === item);
          if (index !== -1) {
            const [selectedCountry] = countries.splice(index, 1);
            countries.unshift(selectedCountry);
          }
        });
        return new GenericApiResponseModel(CountryModel, { ...data, results: countries });
      }),
      tap(res => this.countryFacade.init({ countries: res.results || [] })),
    );
  }

  getNationalities(): Observable<GenericApiResponseModel<NationalityModel>> {
    return this.api.get(this.NATIONALITIES, { limit: 999 }).pipe(
      map(data => {
        const nationalities = data?.items;
        if (!nationalities || nationalities.length === 0) return new GenericApiResponseModel(NationalityModel, data);
        const topListNationalityCodes = ['MY', 'SG'];
        topListNationalityCodes.forEach(item => {
          const index = nationalities.findIndex((country: NationalityModel) => country.sibs === item);
          if (index !== -1) {
            const [selectedCountry] = nationalities.splice(index, 1);
            nationalities.unshift(selectedCountry);
          }
        });

        return new GenericApiResponseModel(NationalityModel, { ...data, results: nationalities });
      }),
      tap(res => {
        res.results = res.results.map((country: NationalityModel) => {
          return { ...country, nationality: titleCase(country.nationality) };
        });
        this.nationalityFacade.init({ nationalities: res.results || [] });
      }),
    );
  }

  getDataSetup(query?: { key: string[] }): Observable<DataSetupModel> {
    return this.api.get(this.DATA_SETUP, query).pipe(
      map(res => DataSetupModel.fromJson(res)),
      tap(dataSetup => this.dataSetupFacade.init({ dataSetup })),
    );
  }
}
